import { Component, computed, inject, signal, OnInit, DestroyRef } from '@angular/core';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, debounceTime, distinctUntilChanged, switchMap, of, forkJoin, finalize } from 'rxjs';
import { SidebarService } from '../../services/sidebar.service';
import { SidebarPanelType } from '../../models/sidebar-panel';
import { SalesOrderService } from '../../../features/sales-orders/services/sales-order.service';
import { CustomerPickupRequestService } from '../../../features/cprs/services/customer-pickup-request.service';
import { LocationStateService } from '../../../features/locations/services/location-state.service';
import { ReportService, BillingSearchResult } from '../../../features/reports/services/report.service';
import { SalesOrder } from '../../../features/sales-orders/models/sales-order';
import { CustomerPickupRequest } from '../../../features/cprs/models/customer-pickup-request';
import { AccountLocation } from '../../../features/locations/models/account-location';
import { getBillingTypeLabel, getOrderStatusLabel } from '../../constants/domain.constants';

@Component({
  selector: 'app-search-results-panel',
  imports: [DatePipe, CurrencyPipe],
  templateUrl: './search-results-panel.component.html',
  styleUrl: './search-results-panel.component.scss'
})
export class SearchResultsPanelComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly sidebarService = inject(SidebarService);
  private readonly salesOrderService = inject(SalesOrderService);
  private readonly cprService = inject(CustomerPickupRequestService);
  private readonly reportService = inject(ReportService);
  private readonly locationStateService = inject(LocationStateService);

  private readonly searchSubject = new Subject<string>();
  private allLocations: AccountLocation[] = [];

  readonly query = signal('');
  readonly loading = signal(false);
  readonly salesOrders = signal<SalesOrder[]>([]);
  readonly cprs = signal<CustomerPickupRequest[]>([]);
  readonly billingResults = signal<BillingSearchResult[]>([]);
  readonly locations = signal<AccountLocation[]>([]);

  readonly salesInvoices = computed(() =>
    this.billingResults().filter((r) => r.billingType === 0)
  );

  readonly otherBillingResults = computed(() =>
    this.billingResults().filter((r) => r.billingType !== 0)
  );

  readonly hasResults = computed(() =>
    this.salesOrders().length > 0 ||
    this.cprs().length > 0 ||
    this.billingResults().length > 0 ||
    this.locations().length > 0
  );

  readonly showEmpty = computed(() =>
    !this.loading() && this.query().length >= 2 && !this.hasResults()
  );

  ngOnInit(): void {
    this.locationStateService.allLocations$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((locs) => (this.allLocations = locs));

    this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((q) => {
          if (q.length < 2) {
            this.salesOrders.set([]);
            this.cprs.set([]);
            this.billingResults.set([]);
            this.locations.set([]);
            return of(null);
          }

          this.loading.set(true);
          const lowerQ = q.toLowerCase();

          const locationResults = this.allLocations.filter(
            (loc) =>
              loc.locationName?.toLowerCase().includes(lowerQ) ||
              loc.addressCity?.toLowerCase().includes(lowerQ)
          );
          this.locations.set(locationResults);

          return forkJoin({
            orders: this.salesOrderService.search(q),
            cprs: this.cprService.search(q),
            billing: this.reportService.searchBillingByText(q)
          }).pipe(finalize(() => this.loading.set(false)));
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (results) => {
          if (results) {
            this.salesOrders.set(results.orders);
            this.cprs.set(results.cprs);
            this.billingResults.set(results.billing);
          }
        },
        error: () => {
          this.salesOrders.set([]);
          this.cprs.set([]);
          this.billingResults.set([]);
          this.loading.set(false);
        }
      });

    // Restore previous search query (survives push/back navigation)
    const savedQuery = this.sidebarService.lastSearchQuery();
    const panel = this.sidebarService.currentPanel();
    const panelQuery = panel?.type === SidebarPanelType.SearchResults ? panel.data?.query : undefined;
    const restoreQuery = savedQuery || panelQuery || '';
    if (restoreQuery) {
      this.query.set(restoreQuery);
      this.searchSubject.next(restoreQuery);
    }
  }

  onInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.query.set(value);
    this.sidebarService.lastSearchQuery.set(value);
    this.searchSubject.next(value);
  }

  clearSearch(): void {
    this.query.set('');
    this.sidebarService.lastSearchQuery.set('');
    this.salesOrders.set([]);
    this.cprs.set([]);
    this.billingResults.set([]);
    this.locations.set([]);
  }

  getOrderStatusLabel(status: number): string {
    return getOrderStatusLabel(status);
  }

  getBillingTypeLabel(billingType: number): string {
    return getBillingTypeLabel(billingType);
  }

  openSalesOrderDetail(order: SalesOrder): void {
    this.sidebarService.push({
      type: SidebarPanelType.SalesOrderDetail,
      data: order
    });
  }

  getCprTotalQuantity(cpr: CustomerPickupRequest): number {
    return (cpr.details ?? []).reduce((sum, d) => sum + (d.quantity ?? 0), 0);
  }

  openCprDetail(cpr: CustomerPickupRequest): void {
    this.sidebarService.push({
      type: SidebarPanelType.CprDetail,
      data: cpr
    });
  }

  openInvoiceDetail(row: BillingSearchResult): void {
    this.sidebarService.push({
      type: SidebarPanelType.InvoiceDetail,
      data: row
    });
  }

  goToLocation(location: AccountLocation): void {
    this.locationStateService.setSelectedLocation(location);
    this.router.navigate(['/dashboard']);
    this.sidebarService.close();
  }
}
